import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import EarningsHeader from './EarningsHeader';
import EarningsHistogram from './EarningsHistogram';
import EarningsDateGroup from './EarningsDateGroup';
import EarningsDayPanel from './EarningsDayPanel';
import { useEarningsData } from './useEarningsData';
import { useHistogramNavigation } from './useHistogramNavigation';
import { useStockNews } from './useStockNews';
import { useCallHighlights, useWatchlistData } from '../../services/api';
import usePullToRefresh from '../../hooks/usePullToRefresh';

const SkeletonTile = ({ index }) => (
  <div
    className="bg-white rounded-lg border-l-[3px] border-l-gray-200 animate-pulse"
    style={{ animationDelay: `${index * 60}ms` }}
  >
    <div className="flex items-center gap-2 px-3 py-2">
      <div className="w-6 h-6 rounded bg-gray-100 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="w-10 h-3.5 rounded bg-gray-100" />
          <div className="w-24 h-3 rounded bg-gray-50" />
        </div>
      </div>
      <div className="w-12 h-3 rounded bg-gray-50 flex-shrink-0" />
      <div className="flex-shrink-0 text-right">
        <div className="w-14 h-3.5 rounded bg-gray-100 mb-0.5" />
        <div className="w-10 h-2.5 rounded bg-gray-50" />
      </div>
      <div className="w-4 h-4 rounded bg-gray-50 flex-shrink-0" />
    </div>
  </div>
);

const MobileEarningsView = ({ loggedInUser, onLoginSuccess, onOpenLabelManager }) => {
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState(() => {
    try { return JSON.parse(localStorage.getItem('earningsIndustryFilter')) || []; } catch { return []; }
  });
  const [expandedTicker, setExpandedTicker] = useState(null);
  const bottomRef = useRef(null);
  const observerRef = useRef(null);
  // Tracks whether the user manually toggled All/Watchlist this session, so the
  // "default to Watchlist when logged in" effect doesn't fight an explicit choice.
  const userToggledWatchlistRef = useRef(false);

  const { data: watchlistData } = useWatchlistData(loggedInUser?.email);
  const watchlistSymbols = useMemo(() => {
    if (!showWatchlist || !watchlistData?.companiesSymbols?.length) return null;
    return watchlistData.companiesSymbols;
  }, [showWatchlist, watchlistData?.companiesSymbols]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetchFresh,
  } = useEarningsData(7, watchlistSymbols, selectedIndustries.length ? selectedIndustries : null, 1);

  const handleRefresh = useCallback(async () => {
    await refetchFresh();
  }, [refetchFresh]);

  const {
    containerRef: ptrRef,
    pullDistance,
    isPulling,
    isRefreshing: ptrRefreshing,
    threshold: ptrThreshold,
    handlers: ptrHandlers,
  } = usePullToRefresh({ onRefresh: handleRefresh });

  const ptrOffset = ptrRefreshing ? 60 : Math.min(pullDistance, 80);
  const ptrVisible = pullDistance > 0 || ptrRefreshing;
  const ptrMessage = ptrRefreshing
    ? 'Refreshing…'
    : pullDistance > ptrThreshold
      ? 'Release to refresh'
      : 'Pull down to refresh';

  // Extract all loaded stock symbols for batch news fetch
  const allSymbols = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => (page.stocks || []).map(s => s.symbol));
  }, [data?.pages]);

  const { data: newsMap } = useStockNews(allSymbols);
  const { data: highlightsMap } = useCallHighlights(allSymbols, loggedInUser?.email);

  const handleToggleTicker = useCallback((ticker) => {
    setExpandedTicker((prev) => (prev === ticker ? null : ticker));
  }, []);

  const handleToggleWatchlist = useCallback(() => {
    userToggledWatchlistRef.current = true;
    setShowWatchlist((prev) => !prev);
    setExpandedTicker(null);
  }, []);

  // Default the filter to Watchlist once the user is logged in (unless they've
  // manually toggled it this session). Revert to All + clear the flag on logout.
  useEffect(() => {
    if (loggedInUser && !userToggledWatchlistRef.current) {
      setShowWatchlist(true);
    } else if (!loggedInUser) {
      userToggledWatchlistRef.current = false;
      setShowWatchlist(false);
    }
  }, [loggedInUser]);

  const handleToggleIndustry = useCallback((industry) => {
    setSelectedIndustries((prev) => {
      const next = prev.includes(industry) ? prev.filter(i => i !== industry) : [...prev, industry];
      localStorage.setItem('earningsIndustryFilter', JSON.stringify(next));
      return next;
    });
    setExpandedTicker(null);
  }, []);

  const handleClearIndustries = useCallback(() => {
    setSelectedIndustries([]);
    localStorage.removeItem('earningsIndustryFilter');
    setExpandedTicker(null);
  }, []);

  const industries = useMemo(() => {
    return data?.pages?.[0]?.industries || [];
  }, [data?.pages]);

  const dateGroups = useMemo(() => {
    if (!data?.pages) return [];
    const allStocks = data.pages.flatMap((page) => page.stocks || []);
    const groups = {};
    for (const stock of allStocks) {
      const raw = stock.next_earnings_date || 'unknown';
      const dateKey = raw.includes('T') ? raw.split('T')[0] : raw;
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(stock);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [data?.pages]);

  useEffect(() => {
    observerRef.current?.disconnect();
    if (!hasNextPage || isFetchingNextPage) return;
    const element = bottomRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          fetchNextPage();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(element);
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, dateGroups.length]);

  const totalCount = data?.pages?.[0]?.total || 0;
  const histogram = data?.pages?.[0]?.histogram || [];

  const { handleBarClick, panelDate, panelBellwethers, closePanel } = useHistogramNavigation({
    data, dateGroups, hasNextPage, isFetchingNextPage, fetchNextPage, histogram,
  });

  return (
    <div
      ref={ptrRef}
      style={{ margin: '0 -16px', touchAction: 'pan-y' }}
      {...ptrHandlers}
    >
      {/* Pull-to-refresh indicator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: '#1d4ed8',
          fontSize: 12,
          fontWeight: 600,
          height: ptrVisible ? 48 : 0,
          opacity: ptrVisible ? 1 : 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          transition: isPulling ? 'none' : 'height 0.2s ease, opacity 0.2s ease',
        }}
      >
        <span
          className={ptrRefreshing ? 'animate-spin' : ''}
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
          }}
        />
        <span>{ptrMessage}</span>
      </div>
      <div
        style={{
          transform: ptrOffset ? `translateY(${ptrOffset}px)` : undefined,
          transition: isPulling ? 'none' : 'transform 0.25s ease',
          willChange: ptrOffset ? 'transform' : undefined,
        }}
      >
      {/* Compact header with filters */}
      <EarningsHeader
        showWatchlist={showWatchlist}
        onToggleWatchlist={handleToggleWatchlist}
        loggedInUser={loggedInUser}
        totalCount={totalCount}
        isLoading={isLoading}
        industries={industries}
        selectedIndustries={selectedIndustries}
        onToggleIndustry={handleToggleIndustry}
        onClearIndustries={handleClearIndustries}
      />

      {/* Content */}
      <div style={{ paddingBottom: 48, paddingTop: 8 }}>
        {/* Histogram */}
        {!isLoading && histogram.length > 0 && (
          <EarningsHistogram histogram={histogram} onBarClick={handleBarClick} selectedDate={panelDate} />
        )}

        {/* Day panel for bar clicks beyond the list window */}
        {panelDate && (
          <EarningsDayPanel
            date={panelDate}
            bellwethers={panelBellwethers}
            onClose={closePanel}
            loggedInUser={loggedInUser}
            onLoginSuccess={onLoginSuccess}
            onOpenLabelManager={onOpenLabelManager}
          />
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="px-4 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonTile key={i} index={i} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && dateGroups.length === 0 && (
          <div style={{ padding: '40px 16px', textAlign: 'center' }}>
            <p style={{ color: '#111827', fontWeight: 600, fontSize: 16 }}>No upcoming earnings</p>
            <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>
              {selectedIndustries.length > 0
                ? 'No stocks in the selected industries report this week'
                : showWatchlist
                  ? 'None of your watchlist stocks report this week'
                  : 'No stocks report earnings this week'}
            </p>
          </div>
        )}

        {/* Date groups */}
        {dateGroups.map(([date, stocks]) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const target = new Date(date.includes('T') ? date : date + 'T00:00:00');
          const isPast = target < today;
          return (
            <EarningsDateGroup
              key={date}
              date={date}
              stocks={stocks}
              loggedInUser={loggedInUser}
              expandedTicker={expandedTicker}
              onToggleTicker={handleToggleTicker}
              onLoginSuccess={onLoginSuccess}
              onOpenLabelManager={onOpenLabelManager}
              defaultCollapsed={isPast}
              newsMap={newsMap}
              highlightsMap={highlightsMap}
            />
          );
        })}

        {/* Infinite scroll sentinel */}
        {hasNextPage && (
          <div ref={bottomRef} style={{ padding: '24px 16px' }}>
            {isFetchingNextPage && (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-gray-400 font-medium">Loading more...</span>
              </div>
            )}
          </div>
        )}

        {/* Filter loading indicator */}
        {isFetching && !isLoading && !isFetchingNextPage && (
          <div style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 30 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 16px', background: '#111827', color: '#fff',
              fontSize: 12, fontWeight: 600, borderRadius: 999,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            }}>
              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default MobileEarningsView;
